import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { entities, uploadFile, parseUploadBlobIdFromFileUrl, getApiUrl, getToken } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  PenLine,
  Plus,
  Send,
  X,
  FileDown,
  ScrollText,
  Loader2,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Mail,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Avisos quando o envelope foi processado mas o e-mail do convite não saiu. */
function toastAssinaInviteResumo(resumo) {
  if (!resumo || typeof resumo !== 'object') return;
  const nao = Number(resumo.nao_enviados) || 0;
  if (nao <= 0) return;
  if (resumo.smtp_nao_configurado) {
    toast.warning(
      'SMTP não configurado no servidor (Super Admin → E-mail/SMTP). Os convites não foram enviados. Configure e use «Reenviar convites por e-mail».'
    );
  } else if (resumo.falha_smtp) {
    toast.warning(
      `Falha ao enviar ${nao} convite(s) por e-mail (SMTP). Verifique host, porta e credenciais no servidor e tente reenviar.`
    );
  } else {
    toast.warning('Alguns convites não foram entregues por e-mail.');
  }
}

/** Resumo do envio do e-mail de conclusão (PDF assinado). */
function toastAssinaCompletionResumo(resumo) {
  if (!resumo || typeof resumo !== 'object') return;
  const attempted = Number(resumo.attempted) || 0;
  const sent = Number(resumo.sent) || 0;
  const failed = Number(resumo.failed) || 0;
  if (attempted === 0) {
    toast.info('Nenhuma tentativa de envio de e-mail de conclusão nesta operação.');
    return;
  }
  if (resumo.smtpUnavailable) {
    toast.warning(
      'SMTP indisponível ou não configurado. O PDF assinado não foi enviado por e-mail. Configure em Super Admin e tente de novo.'
    );
    return;
  }
  if (sent === attempted && sent > 0) {
    toast.success(`E-mail com PDF assinado enviado para ${sent} destinatário(s).`);
    return;
  }
  toast.warning(
    `Envio parcial: ${sent} entregue(s), ${failed} falha(s). Verifique o SMTP ou tente reenviar.`
  );
}

const statusLabel = {
  rascunho: 'Rascunho',
  enviado: 'Enviado',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
  expirado: 'Expirado',
};

const signatarioStatusLabel = {
  pendente: 'Pendente',
  visualizado: 'Visualizado',
  assinado: 'Assinado',
  rejeitado: 'Rejeitado',
};

function formatAssinaDateTime(value) {
  if (value == null) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return null;
  }
}

/** Normaliza campos camelCase (API) ou snake_case. */
function normalizeSignatario(s) {
  return {
    id: s.id,
    nome: s.nome,
    email: s.email,
    status: s.status,
    ordem: s.ordemAssinatura ?? s.ordem_assinatura ?? 0,
    nivel: s.nivel,
    papel: s.papel,
    cpfCnpj: s.cpfCnpj ?? s.cpf_cnpj,
    signedAt: s.signedAt ?? s.signed_at,
    rejectedAt: s.rejectedAt ?? s.rejected_at,
    motivoRejeicao: s.motivoRejeicao ?? s.motivo_rejeicao,
    signerIp: s.signerIp ?? s.signer_ip,
  };
}

/** Fluxo circulatório: assinados primeiro, por data/hora da assinatura; demais pela ordem de cadastro. */
function signedAtMsParaOrdenacao(sig) {
  if (sig.status !== 'assinado' || sig.signedAt == null) return null;
  const t = new Date(sig.signedAt).getTime();
  return Number.isFinite(t) ? t : null;
}

function signatarioStatusBadgeVariant(st) {
  if (st === 'assinado') return 'default';
  if (st === 'rejeitado') return 'destructive';
  if (st === 'visualizado') return 'secondary';
  return 'outline';
}

function emailKeyAssina(email) {
  return String(email ?? '')
    .trim()
    .toLowerCase();
}

/** Lista completa para addSigners: já persistidos + locais (sem repetir e-mail já na lista). */
function buildSignatariosPayloadMerge(detail, signatariosLocal) {
  const raw = Array.isArray(detail?.signatarios) ? detail.signatarios : [];
  const seen = new Set();
  const fromServer = [];
  for (const s of raw) {
    const o = normalizeSignatario(s);
    const key = emailKeyAssina(o.email);
    if (!key) continue;
    seen.add(key);
    const row = {
      nome: o.nome,
      email: o.email,
      ordem_assinatura: o.ordem,
      nivel: o.nivel === 'avancado' || o.nivel === 'qualificada' ? o.nivel : 'simples',
    };
    if (o.cpfCnpj) row.cpf_cnpj = o.cpfCnpj;
    if (o.papel) row.papel = o.papel;
    fromServer.push(row);
  }
  const maxOrd = fromServer.reduce((m, r) => Math.max(m, Number(r.ordem_assinatura) || 0), -1);
  let next = maxOrd + 1;
  const fromLocal = [];
  for (const s of signatariosLocal) {
    const key = emailKeyAssina(s.email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    fromLocal.push({
      nome: s.nome.trim(),
      email: s.email.trim(),
      ordem_assinatura: next++,
      nivel: 'simples',
    });
  }
  return [...fromServer, ...fromLocal];
}

const PAGE_SIZE = 10;

const ENVELOPE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Cartão com faixa azul (passos do fluxo em rascunho). */
function AssinaPassoCard({ titulo, children }) {
  return (
    <section className="rounded-xl border border-border/70 bg-card text-card-foreground shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 sm:px-5 bg-sky-100/90 dark:bg-sky-950/45 border-b border-sky-200/70 dark:border-sky-900/60">
        <h3 className="text-sm font-semibold tracking-tight text-sky-950 dark:text-sky-50">{titulo}</h3>
      </div>
      <div className="p-4 sm:p-5 space-y-3">{children}</div>
    </section>
  );
}

export default function AssinaPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [novoOpen, setNovoOpen] = useState(false);
  const [tituloNovo, setTituloNovo] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [signNome, setSignNome] = useState('');
  const [signEmail, setSignEmail] = useState('');
  const [signatariosLocal, setSignatariosLocal] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [consultaInput, setConsultaInput] = useState('');
  const [consultaQ, setConsultaQ] = useState('');
  const [confirmGravarOpen, setConfirmGravarOpen] = useState(false);
  const [enviarAssinaturaPending, setEnviarAssinaturaPending] = useState(false);

  useEffect(() => {
    const raw = searchParams.get('envelope');
    if (!raw) return;
    const id = decodeURIComponent(raw).trim();
    if (!ENVELOPE_UUID_RE.test(id)) {
      toast.error('Parâmetro de envelope inválido na URL.');
      const next = new URLSearchParams(searchParams);
      next.delete('envelope');
      setSearchParams(next, { replace: true });
      return;
    }
    setSelectedId(id);
    void qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    const next = new URLSearchParams(searchParams);
    next.delete('envelope');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, qc]);

  const { data: listPayload, isLoading, refetch } = useQuery({
    queryKey: ['assina-envelopes', listPage, consultaQ],
    queryFn: () => entities.Assina.list({ page: listPage, limit: PAGE_SIZE, q: consultaQ || undefined }),
  });
  const list = listPayload?.items ?? [];
  const listTotal = listPayload?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));

  const {
    data: detail,
    isLoading: loadingDetail,
    isFetching: fetchingDetail,
    refetch: refetchEnvelopeDetail,
  } = useQuery({
    queryKey: ['assina-envelope', selectedId],
    queryFn: () => entities.Assina.get(selectedId),
    enabled: !!selectedId,
  });

  const criarEnvelope = async () => {
    try {
      const created = await entities.Assina.create({ titulo: tituloNovo || null });
      const newId = created?.id;
      toast.success('Envelope criado');
      setTituloNovo('');
      setNovoOpen(false);
      if (newId) setSelectedId(newId);
      setListPage(1);
      setConsultaQ('');
      setConsultaInput('');
      await qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
      if (newId) await qc.invalidateQueries({ queryKey: ['assina-envelope', newId] });
    } catch (e) {
      toast.error(e?.message || 'Erro ao criar envelope');
    }
  };

  const anexarArquivo = async (file) => {
    if (!selectedId || !file) return;
    try {
      const up = await uploadFile(file);
      const blobId = parseUploadBlobIdFromFileUrl(up.file_url);
      if (!blobId) {
        toast.error('Não foi possível obter o id do arquivo. Verifique a resposta do upload.');
        return;
      }
      await entities.Assina.addDocument(selectedId, blobId);
      toast.success('Documento anexado');
      qc.invalidateQueries({ queryKey: ['assina-envelope', selectedId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    } catch (e) {
      toast.error(e?.message || 'Erro no upload');
    }
  };

  const removeDocumentMutation = useMutation({
    mutationFn: ({ envelopeId, documentoId }) => entities.Assina.removeDocument(envelopeId, documentoId),
    onSuccess: (_, { envelopeId }) => {
      toast.success('Documento removido. Você pode anexar outro PDF.');
      qc.invalidateQueries({ queryKey: ['assina-envelope', envelopeId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    },
    onError: (e) => {
      toast.error(e?.message || 'Erro ao remover documento');
    },
  });

  const reenviarConvitesMutation = useMutation({
    mutationFn: (envelopeId) => entities.Assina.resendInvites(envelopeId),
    onSuccess: (data, envelopeId) => {
      toast.success('Reenvio de convites concluído.');
      toastAssinaInviteResumo(data?.invite_email_resumo);
      qc.invalidateQueries({ queryKey: ['assina-envelope', envelopeId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    },
    onError: (e) => {
      toast.error(e?.message || 'Erro ao reenviar convites');
    },
  });

  const reenviarEmailConclusaoMutation = useMutation({
    mutationFn: (envelopeId) => entities.Assina.resendCompletionEmails(envelopeId),
    onSuccess: (data, envelopeId) => {
      toast.success('Reenvio de e-mail de conclusão processado.');
      toastAssinaCompletionResumo(data?.completion_email_resumo);
      qc.invalidateQueries({ queryKey: ['assina-envelope', envelopeId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    },
    onError: (e) => {
      toast.error(e?.message || 'Erro ao reenviar e-mail com PDF assinado');
    },
  });

  const adicionarSignatarioLista = () => {
    if (!signNome.trim() || !signEmail.trim()) {
      toast.error('Preencha nome e e-mail');
      return;
    }
    setSignatariosLocal((prev) => [...prev, { nome: signNome.trim(), email: signEmail.trim() }]);
    setSignNome('');
    setSignEmail('');
  };

  const solicitarConfirmacaoGravarSignatarios = () => {
    if (!selectedId || signatariosLocal.length === 0) {
      toast.error('Inclua ao menos um signatário');
      return;
    }
    if (detail?.status && detail.status !== 'rascunho') {
      toast.error(
        'Este envelope já foi enviado ou encerrado. Não é possível alterar signatários. Atualize a página ou selecione outro envelope em rascunho.'
      );
      void refetchEnvelopeDetail();
      return;
    }
    setConfirmGravarOpen(true);
  };

  const executarSalvarSignatarios = async () => {
    if (!selectedId || signatariosLocal.length === 0) return;
    setConfirmGravarOpen(false);
    try {
      const payload = buildSignatariosPayloadMerge(detail, signatariosLocal);
      await entities.Assina.addSigners(selectedId, payload);
      toast.success('Signatários salvos');
      setSignatariosLocal([]);
      qc.invalidateQueries({ queryKey: ['assina-envelope', selectedId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    } catch (e) {
      toast.error(e?.message || 'Erro ao salvar signatários');
    }
  };

  const enviar = async () => {
    if (!selectedId) return;
    const docCount = detail?.documentos?.length ?? 0;
    if (docCount === 0) {
      toast.error('Anexe ao menos um PDF ao envelope antes de enviar para assinatura.');
      return;
    }
    const localPending = signatariosLocal.length;
    const merged = buildSignatariosPayloadMerge(detail, signatariosLocal);
    const signatariosParaEnvio = merged.length;
    if (signatariosParaEnvio === 0) {
      toast.error('Inclua ao menos um signatário antes de enviar (use Gravar ou envie com a lista preenchida).');
      return;
    }
    if (detail?.status && detail.status !== 'rascunho') {
      toast.error(
        'Este envelope não está mais em rascunho. Atualize a página para ver o status atual.'
      );
      void refetchEnvelopeDetail();
      return;
    }
    setEnviarAssinaturaPending(true);
    try {
      if (localPending > 0) {
        await entities.Assina.addSigners(selectedId, merged);
        setSignatariosLocal([]);
      }
      const enviado = await entities.Assina.send(selectedId);
      toast.success('Envelope enviado para assinatura.');
      toastAssinaInviteResumo(enviado?.invite_email_resumo);
      qc.invalidateQueries({ queryKey: ['assina-envelope', selectedId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    } catch (e) {
      toast.error(e?.message || 'Erro ao enviar');
    } finally {
      setEnviarAssinaturaPending(false);
    }
  };

  const cancelar = async () => {
    if (!selectedId) return;
    try {
      await entities.Assina.cancel(selectedId);
      toast.success('Envelope cancelado');
      qc.invalidateQueries({ queryKey: ['assina-envelope', selectedId] });
      qc.invalidateQueries({ queryKey: ['assina-envelopes'] });
    } catch (e) {
      toast.error(e?.message || 'Erro ao cancelar');
    }
  };

  const baixar = async () => {
    if (!selectedId) return;
    const base = getApiUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
    const url = `${base}/api/assina/envelopes/${selectedId}/download`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Falha ao baixar');
      const blob = await res.blob();
      const d = await res.headers.get('Content-Disposition');
      let name = 'documento.pdf';
      const m = d && /filename="([^"]+)"/.exec(d);
      if (m) name = m[1];
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Não foi possível baixar o documento');
    }
  };

  const carregarAuditoria = async () => {
    if (!selectedId) return;
    try {
      const rows = await entities.Assina.audit(selectedId);
      setAuditRows(Array.isArray(rows) ? rows : []);
      setAuditOpen(true);
    } catch {
      toast.error('Erro ao carregar auditoria');
    }
  };

  const atualizarDetalheEnvelope = async () => {
    if (!selectedId) return;
    const [rDet, rLista] = await Promise.all([refetchEnvelopeDetail(), refetch()]);
    if (rDet.isError || rLista.isError) {
      toast.error('Não foi possível atualizar o envelope');
    }
  };

  const docCount = detail?.documentos?.length ?? 0;
  const sigCount = detail?.signatarios?.length ?? 0;
  const status = detail?.status || '';
  const signatariosOrdenados = React.useMemo(() => {
    const raw = detail?.signatarios;
    if (!Array.isArray(raw)) return [];
    return [...raw]
      .map(normalizeSignatario)
      .sort((a, b) => {
        const ta = signedAtMsParaOrdenacao(a);
        const tb = signedAtMsParaOrdenacao(b);
        if (ta != null && tb != null) return ta - tb;
        if (ta != null && tb == null) return -1;
        if (ta == null && tb != null) return 1;
        return a.ordem - b.ordem;
      });
  }, [detail?.signatarios]);

  const podeReenviarConvites =
    status === 'enviado' &&
    signatariosOrdenados.some((s) => s.status === 'pendente' || s.status === 'visualizado');

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-tour="tour-Assina-root">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PenLine className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Assina</h1>
            <p className="text-sm text-muted-foreground">
              Assinatura por OTP (nível simples), fluxo circulatório: todos os signatários podem assinar ao mesmo tempo.
            </p>
          </div>
        </div>
        <Button onClick={() => setNovoOpen(true)} data-tour="tour-Assina-primary">
          <Plus className="h-4 w-4 mr-2" />
          Novo envelope
        </Button>
      </div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] lg:items-start">
        <Card className="min-w-0">
          <CardHeader className="space-y-3">
            <CardTitle className="text-lg">Envelopes</CardTitle>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="assina-consulta-envelopes" className="text-xs text-muted-foreground">
                  Consulta (título ou ID do envelope)
                </Label>
                <Input
                  id="assina-consulta-envelopes"
                  value={consultaInput}
                  onChange={(e) => setConsultaInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setListPage(1);
                      setConsultaQ(consultaInput.trim());
                    }
                  }}
                  placeholder="Buscar…"
                  className="h-9"
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  setListPage(1);
                  setConsultaQ(consultaInput.trim());
                }}
              >
                <Search className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Buscar</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading && (
              <div className="flex justify-center py-8 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
            {!isLoading && list.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {consultaQ ? 'Nenhum envelope encontrado para esta consulta.' : 'Nenhum envelope ainda.'}
              </p>
            )}
            {list.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelectedId(row.id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  selectedId === row.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
              >
                <div className="flex justify-between gap-2">
                  <span className="font-medium truncate">{row.titulo || 'Sem título'}</span>
                  <Badge variant="secondary">{statusLabel[row.status] || row.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {row._count?.documentos ?? '?'} doc(s) · {row._count?.signatarios ?? '?'} signatário(s)
                </p>
              </button>
            ))}
            {!isLoading && listTotal > 0 && (
              <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {listTotal} envelope(s) · página {listPage} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={listPage <= 1}
                    onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Anterior</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={listPage >= totalPages}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <ChevronRight className="h-4 w-4 sm:ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0 border-border/80 shadow-md">
          <CardContent className="pt-6 pb-6 space-y-6">
            {!selectedId && (
              <p className="text-sm text-muted-foreground">Selecione um envelope à esquerda.</p>
            )}
            {selectedId && loadingDetail && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
              </div>
            )}
            {selectedId && detail && (
              <>
                <header className="space-y-4">
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold tracking-tight text-foreground">Detalhe do envelope</h2>
                    <p className="text-sm text-muted-foreground">
                      {detail.titulo?.trim() ? detail.titulo.trim() : 'Sem título definido'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ID</p>
                      <p className="font-mono text-[11px] sm:text-xs leading-relaxed break-all text-foreground/90 bg-muted/50 rounded-lg px-3 py-2 border border-border/60">
                        {detail.id}
                      </p>
                    </div>
                    <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                      <Badge
                        className={cn(
                          'w-fit rounded-full px-3 py-0.5 text-xs font-medium border-0 shadow-none',
                          status === 'rascunho' && 'bg-foreground text-background hover:bg-foreground/90',
                          status !== 'rascunho' && 'bg-secondary text-secondary-foreground'
                        )}
                      >
                        {statusLabel[status] || status}
                      </Badge>
                      <p className="text-sm text-muted-foreground sm:text-right tabular-nums">
                        {docCount} documento(s), {sigCount} signatário(s)
                      </p>
                    </div>
                  </div>
                </header>

                <div className="rounded-xl border border-border/70 bg-card shadow-sm p-4 sm:p-5 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-base font-semibold text-foreground">Lista de signatários</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void atualizarDetalheEnvelope()}
                      disabled={loadingDetail || fetchingDetail}
                      title="Atualizar status dos signatários e dados do envelope"
                    >
                      <RefreshCw
                        className={`h-4 w-4 sm:mr-2 ${fetchingDetail ? 'animate-spin' : ''}`}
                        aria-hidden
                      />
                      <span className="hidden sm:inline">Atualizar</span>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Quem assinou primeiro aparece primeiro; pendências seguem a ordem em que foram cadastrados no
                    envelope.
                  </p>
                  {signatariosOrdenados.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-1">Nenhum signatário cadastrado neste envelope.</p>
                  ) : (
                    <ul className="space-y-3 text-sm">
                      {signatariosOrdenados.map((sig, idx) => (
                        <li
                          key={sig.id}
                          className="rounded-lg border border-border/60 bg-background/80 p-3 space-y-1.5 shadow-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">
                              {idx + 1}. {sig.nome}
                            </span>
                            <Badge variant={signatarioStatusBadgeVariant(sig.status)}>
                              {signatarioStatusLabel[sig.status] || sig.status}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground break-all">{sig.email}</p>
                          <div className="grid gap-0.5 text-xs text-muted-foreground sm:grid-cols-2">
                            {sig.papel ? (
                              <span>
                                <span className="font-medium text-foreground/80">Papel:</span> {sig.papel}
                              </span>
                            ) : null}
                            <span>
                              <span className="font-medium text-foreground/80">Nível:</span>{' '}
                              {sig.nivel === 'simples'
                                ? 'Simples'
                                : sig.nivel === 'avancado'
                                  ? 'Avançado'
                                  : sig.nivel === 'qualificada'
                                    ? 'Qualificada'
                                    : sig.nivel || '—'}
                            </span>
                            {sig.cpfCnpj ? (
                              <span>
                                <span className="font-medium text-foreground/80">CPF/CNPJ:</span> {sig.cpfCnpj}
                              </span>
                            ) : null}
                            {sig.status === 'assinado' && formatAssinaDateTime(sig.signedAt) ? (
                              <span className="sm:col-span-2">
                                <span className="font-medium text-foreground/80">Assinado em:</span>{' '}
                                {formatAssinaDateTime(sig.signedAt)} (horário local)
                              </span>
                            ) : null}
                            {sig.status === 'rejeitado' && formatAssinaDateTime(sig.rejectedAt) ? (
                              <span className="sm:col-span-2">
                                <span className="font-medium text-foreground/80">Rejeitado em:</span>{' '}
                                {formatAssinaDateTime(sig.rejectedAt)}
                              </span>
                            ) : null}
                            {sig.signerIp ? (
                              <span className="sm:col-span-2 font-mono text-[11px]">
                                IP: {sig.signerIp}
                              </span>
                            ) : null}
                            {sig.status === 'rejeitado' && sig.motivoRejeicao ? (
                              <p className="sm:col-span-2 text-destructive text-xs pt-1">
                                <span className="font-medium">Motivo:</span> {sig.motivoRejeicao}
                              </p>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {status === 'rascunho' && (
                  <div className="space-y-4">
                    <AssinaPassoCard titulo="Passo 01 — Selecione o anexo">
                      <div className="space-y-2">
                        <Label htmlFor="assina-anexo-pdf" className="text-sm font-medium">
                          Anexar PDF (um por envelope)
                        </Label>
                        {Array.isArray(detail.documentos) && detail.documentos.length > 0 ? (
                          <>
                            <ul className="space-y-2 text-sm">
                              {detail.documentos.map((d, idx) => {
                                const nome = d.nomeArquivo ?? d.nome_arquivo ?? 'documento.pdf';
                                const removendo =
                                  removeDocumentMutation.isPending &&
                                  removeDocumentMutation.variables?.documentoId === d.id;
                                return (
                                  <li
                                    key={d.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5"
                                  >
                                    <span className="min-w-0 truncate font-medium" title={nome}>
                                      {idx + 1}. {nome}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="shrink-0 text-destructive hover:text-destructive"
                                      disabled={removendo}
                                      onClick={() => {
                                        if (!selectedId) return;
                                        removeDocumentMutation.mutate({
                                          envelopeId: selectedId,
                                          documentoId: d.id,
                                        });
                                      }}
                                    >
                                      {removendo ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                      ) : (
                                        <Trash2 className="h-4 w-4" aria-hidden />
                                      )}
                                      <span className="ml-2">Remover</span>
                                    </Button>
                                  </li>
                                );
                              })}
                            </ul>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Apenas um PDF por envelope. Remova este arquivo para poder anexar outro. Após «Enviar para
                              assinatura», não é possível alterar.
                            </p>
                          </>
                        ) : (
                          <>
                            <Input
                              id="assina-anexo-pdf"
                              type="file"
                              accept=".pdf,application/pdf"
                              className="cursor-pointer file:mr-4 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted/80"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void anexarArquivo(f);
                                e.target.value = '';
                              }}
                            />
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Só é permitido um PDF por envelope. Depois de anexar, use Remover na lista para poder enviar
                              outro arquivo.
                            </p>
                          </>
                        )}
                      </div>
                    </AssinaPassoCard>

                    <AssinaPassoCard titulo="Passo 02 — Selecione os signatários">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium">Signatários (fluxo circulatório)</Label>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Todos recebem o convite e podem assinar em paralelo; a ordem no PDF final segue a data/hora de
                            cada assinatura.
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
                          <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
                            <div className="space-y-1.5">
                              <Label htmlFor="assina-sign-nome" className="text-xs text-muted-foreground">
                                Nome
                              </Label>
                              <Input
                                id="assina-sign-nome"
                                placeholder="Nome completo"
                                value={signNome}
                                onChange={(e) => setSignNome(e.target.value)}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="assina-sign-email" className="text-xs text-muted-foreground">
                                E-mail
                              </Label>
                              <Input
                                id="assina-sign-email"
                                placeholder="email@empresa.com"
                                type="email"
                                value={signEmail}
                                onChange={(e) => setSignEmail(e.target.value)}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              className="lg:shrink-0"
                              onClick={adicionarSignatarioLista}
                            >
                              Adicionar
                            </Button>
                          </div>
                        </div>
                        {signatariosLocal.length > 0 && (
                          <ul className="rounded-lg border border-dashed border-border/80 bg-muted/15 px-3 py-2 text-sm space-y-1">
                            {signatariosLocal.map((s, i) => (
                              <li key={`${s.email}-${i}`}>
                                <span className="font-medium text-foreground">{i + 1}.</span> {s.nome}{' '}
                                <span className="text-muted-foreground">— {s.email}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <Button
                          type="button"
                          variant="default"
                          className="w-full sm:w-auto font-semibold shadow-sm ring-2 ring-primary/20"
                          onClick={solicitarConfirmacaoGravarSignatarios}
                          disabled={signatariosLocal.length === 0}
                        >
                          Gravar signatários no envelope
                        </Button>
                      </div>
                    </AssinaPassoCard>

                    <AssinaPassoCard titulo="Passo 03 — Enviar para assinatura">
                      <div className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                          <Button
                            type="button"
                            onClick={() => void enviar()}
                            className="sm:min-w-[200px]"
                            disabled={enviarAssinaturaPending}
                            aria-busy={enviarAssinaturaPending}
                          >
                            {enviarAssinaturaPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                            ) : (
                              <Send className="h-4 w-4 mr-2" aria-hidden />
                            )}
                            {enviarAssinaturaPending ? 'Enviando…' : 'Enviar para assinatura'}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => void cancelar()} className="sm:min-w-[180px]">
                            <X className="h-4 w-4 mr-2" />
                            Cancelar envelope
                          </Button>
                        </div>
                        <Separator />
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                          <Button type="button" variant="outline" size="sm" onClick={() => void baixar()}>
                            <FileDown className="h-4 w-4 mr-2" />
                            Baixar 1º PDF
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => void carregarAuditoria()}>
                            <ScrollText className="h-4 w-4 mr-2" />
                            Trilha de auditoria
                          </Button>
                        </div>
                      </div>
                    </AssinaPassoCard>
                  </div>
                )}

                {podeReenviarConvites && selectedId && (
                  <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/90 p-4 dark:border-amber-900/60 dark:bg-amber-950/25">
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full sm:w-auto"
                      disabled={reenviarConvitesMutation.isPending}
                      onClick={() => reenviarConvitesMutation.mutate(selectedId)}
                    >
                      {reenviarConvitesMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" aria-hidden />
                      )}
                      Reenviar convites por e-mail
                    </Button>
                  </div>
                )}

                {status === 'concluido' && selectedId && (
                  <div className="space-y-2 rounded-xl border border-sky-200 bg-sky-50/90 p-4 dark:border-sky-900/50 dark:bg-sky-950/20">
                    <p className="text-sm text-foreground leading-relaxed">
                      Após todas as assinaturas, o sistema envia o PDF final por e-mail aos signatários e ao criador do
                      envelope. Se a caixa não recebeu, confira o SMTP e use o botão abaixo (força novo envio).
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full sm:w-auto"
                      disabled={reenviarEmailConclusaoMutation.isPending}
                      onClick={() => reenviarEmailConclusaoMutation.mutate(selectedId)}
                    >
                      {reenviarEmailConclusaoMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" aria-hidden />
                      )}
                      Reenviar e-mail com PDF assinado
                    </Button>
                  </div>
                )}

                {status !== 'rascunho' && (
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center pt-1">
                    {status !== 'concluido' && status !== 'cancelado' && (
                      <Button type="button" variant="outline" onClick={() => void cancelar()} className="w-full sm:w-auto">
                        <X className="h-4 w-4 mr-2" />
                        Cancelar envelope
                      </Button>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap flex-1">
                      <Button type="button" variant="outline" size="sm" onClick={() => void baixar()}>
                        <FileDown className="h-4 w-4 mr-2" />
                        {status === 'concluido' ? 'Baixar PDF assinado' : 'Baixar 1º PDF'}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => void carregarAuditoria()}>
                        <ScrollText className="h-4 w-4 mr-2" />
                        Trilha de auditoria
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Auditoria (cadeia de hashes)</DialogTitle>
          </DialogHeader>
          <ul className="text-xs space-y-2 font-mono">
            {auditRows.map((r) => (
              <li key={r.id} className="border-b pb-2">
                <div className="text-muted-foreground">{new Date(r.createdAt).toISOString()}</div>
                <div className="font-semibold">{r.tipo}</div>
                <div className="break-all text-[10px] opacity-80">{r.chainHash}</div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmGravarOpen} onOpenChange={setConfirmGravarOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gravar signatários</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Confirma que estes são todos os signatários do documento ou deseja adicionar outros?
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setConfirmGravarOpen(false)}>
              Não, adicionar outros
            </Button>
            <Button type="button" onClick={() => void executarSalvarSignatarios()}>
              Sim, gravar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent className="sm:max-w-md gap-0 p-0 overflow-hidden border-border/80 shadow-lg">
          <div className="bg-sky-100/90 dark:bg-sky-950/40 px-6 py-5 border-b border-sky-200/70 dark:border-sky-900/50">
            <DialogHeader className="space-y-2 text-left">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background/80 shadow-sm border border-sky-200/60 dark:border-sky-800/50">
                  <PenLine className="h-5 w-5 text-sky-800 dark:text-sky-200" aria-hidden />
                </div>
                <DialogTitle className="text-lg font-semibold tracking-tight text-sky-950 dark:text-sky-50">
                  Novo envelope
                </DialogTitle>
              </div>
              <DialogDescription className="text-left text-sm text-sky-900/80 dark:text-sky-200/90 pl-[52px]">
                Defina um título para identificar o documento. Em seguida você anexa o PDF, inclui os signatários e
                envia para assinatura.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-5 space-y-4 bg-card">
            <div className="space-y-2">
              <Label htmlFor="assina-novo-envelope-titulo" className="text-sm font-medium">
                Título do documento
              </Label>
              <Input
                id="assina-novo-envelope-titulo"
                value={tituloNovo}
                onChange={(e) => setTituloNovo(e.target.value)}
                placeholder="Ex.: Contrato de prestação de serviços"
                className="h-10"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void criarEnvelope();
                  }
                }}
                autoFocus
              />
              <p className="text-xs text-muted-foreground leading-relaxed">
                O título aparece na lista de envelopes e no detalhe; não substitui o nome do arquivo PDF.
              </p>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 bg-muted/30 border-t border-border/60 sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setNovoOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void criarEnvelope()}>
              Criar envelope
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
